import type {
  CheckoutDialogData,
  CheckoutFlowChrome,
  CheckoutPollingIds,
  CheckoutRequest,
  SubscriptionCheckoutRequest,
} from '@Components/checkout-flow/checkout-flow.types';
import {
  CHECKOUT_DIALOG_MIN_VIEWPORT_WIDTH,
  CHECKOUT_COMPACT_LAYOUT_MAX_WIDTH,
  CHECKOUT_MODAL_HEIGHT,
  CHECKOUT_MODAL_MOBILE_HEIGHT,
  CHECKOUT_MODAL_WIDTH_BY_STEP,
  CHECKOUT_REDIRECT_LINK_DISABLED_SECONDS,
  CheckoutDialogDataMode,
  CheckoutMode,
  CheckoutStep,
  CREDIT_PACKS_COMPACT_MOBILE_HEIGHT,
  CREDIT_PACKS_TABLET_DIALOG_HEIGHT,
  CREDIT_PACKS_TWO_COLUMN_MODAL_HEIGHT,
  CREDIT_PACKS_TWO_COLUMN_MODAL_WIDTH,
  STRIPE_TWO_COLUMN_MIN_VIEWPORT_WIDTH,
  STRIPE_TWO_COLUMN_MODAL_WIDTH,
  STRIPE_STEP_MODAL_MAX_HEIGHT,
} from '@Components/checkout-flow/checkout-flow.types';
import {assertNever} from '@Utils/assert-never.util';

export const isModernCheckoutEnabled = (): boolean => {
  return window.PMW.IS_MODERN_CHECKOUT_ENABLED;
};

export const isCompactCheckoutLayout = (windowWidth: number): boolean => {
  return windowWidth <= CHECKOUT_COMPACT_LAYOUT_MAX_WIDTH;
};

export const isCheckoutBottomSheet = (windowWidth: number): boolean => {
  return windowWidth < CHECKOUT_DIALOG_MIN_VIEWPORT_WIDTH;
};

export const getModalWidthForStep = (step: CheckoutStep, windowWidth: number): string => {
  if (isTwoColumnCreditPacksStep(step, windowWidth)) {
    return CREDIT_PACKS_TWO_COLUMN_MODAL_WIDTH;
  }
  if (isStripeFormStep(step) && windowWidth >= STRIPE_TWO_COLUMN_MIN_VIEWPORT_WIDTH) {
    return STRIPE_TWO_COLUMN_MODAL_WIDTH;
  }
  return CHECKOUT_MODAL_WIDTH_BY_STEP[step];
};

export const getModalHeightForStep = (step: CheckoutStep, windowWidth: number): string => {
  if (isTwoColumnCreditPacksStep(step, windowWidth)) {
    return CREDIT_PACKS_TWO_COLUMN_MODAL_HEIGHT;
  }
  if (step === CheckoutStep.CREDIT_PACKS && !isCheckoutBottomSheet(windowWidth)) {
    return CREDIT_PACKS_TABLET_DIALOG_HEIGHT;
  }
  return CHECKOUT_MODAL_HEIGHT;
};

/** Only the Stripe steps need the promo pill's clearance; every other step keeps the modal default. */
export const getModalMaxHeightForStep = (step: CheckoutStep): string | undefined => {
  return isStripeFormStep(step) ? STRIPE_STEP_MODAL_MAX_HEIGHT : undefined;
};

/** The compact pack list is a short sheet; its full-screen benefits view falls back to the default mobile height. */
export const getModalMobileHeightForStep = (step: CheckoutStep, windowWidth: number, isCreditBenefitsViewOpen: boolean): string => {
  if (step === CheckoutStep.CREDIT_PACKS && isCompactCheckoutLayout(windowWidth) && !isCreditBenefitsViewOpen) {
    return CREDIT_PACKS_COMPACT_MOBILE_HEIGHT;
  }
  return CHECKOUT_MODAL_MOBILE_HEIGHT;
};

const isTwoColumnCreditPacksStep = (step: CheckoutStep, windowWidth: number): boolean => {
  return step === CheckoutStep.CREDIT_PACKS && !isCompactCheckoutLayout(windowWidth);
};

/** Finalizing follows the embedded form, so it keeps the same width and the modal does not resize mid-payment. */
const isStripeFormStep = (step: CheckoutStep): boolean => {
  return step === CheckoutStep.STRIPE_EMBEDDED || step === CheckoutStep.FINALIZING;
};

/**
 * Which close affordance a step shows: the panel header (title + close) or the panel's floating close icon.
 * The confirmation step and the wide credit-pack layout draw their own title, and the compact credit-benefits
 * view carries its own back/close bar.
 */
export const getFlowChrome = (step: CheckoutStep, windowWidth: number, isCreditBenefitsViewOpen: boolean): CheckoutFlowChrome => {
  if (step === CheckoutStep.PREMIUM_CONFIRMATION) {
    return {showsPanelHeader: false, showsPanelCloseIcon: true};
  }
  if (step === CheckoutStep.CREDIT_PACKS) {
    if (isCompactCheckoutLayout(windowWidth)) {
      return {showsPanelHeader: !isCreditBenefitsViewOpen, showsPanelCloseIcon: false};
    }
    return {showsPanelHeader: false, showsPanelCloseIcon: true};
  }
  return {showsPanelHeader: true, showsPanelCloseIcon: false};
};

export const getDialogDataModeForRequest = (request: CheckoutRequest): CheckoutDialogDataMode => {
  switch (request.mode) {
    case CheckoutMode.SUBSCRIPTION:
      return CheckoutDialogDataMode.SUBSCRIPTION;
    case CheckoutMode.CREDIT_PACK:
    case CheckoutMode.DOWNLOAD:
    case CheckoutMode.PAYMENT_PROCESSING:
      return CheckoutDialogDataMode.PAYMENT;
    case CheckoutMode.PREMIUM_CONFIRMATION:
      return CheckoutDialogDataMode.PREMIUM_CONFIRMATION;
    case CheckoutMode.WELCOME_TO_TEAM:
      return CheckoutDialogDataMode.WELCOME_TO_TEAM;
    default:
      return assertNever(request);
  }
};

export const getInitialStepForRequest = (request: CheckoutRequest, dialogData: CheckoutDialogData): CheckoutStep => {
  switch (request.mode) {
    case CheckoutMode.PAYMENT_PROCESSING:
      return CheckoutStep.FINALIZING;
    case CheckoutMode.PREMIUM_CONFIRMATION:
    case CheckoutMode.WELCOME_TO_TEAM:
      return CheckoutStep.PREMIUM_CONFIRMATION;
    case CheckoutMode.SUBSCRIPTION:
    case CheckoutMode.CREDIT_PACK:
    case CheckoutMode.DOWNLOAD:
      return getInitialPurchaseStep(dialogData);
    default:
      return assertNever(request);
  }
};

export const getStepAfterEmailAdded = (dialogData: CheckoutDialogData): CheckoutStep => {
  return getInitialPurchaseStep({...dialogData, isEmailRequired: false});
};

const getInitialPurchaseStep = (dialogData: CheckoutDialogData): CheckoutStep => {
  if (dialogData.isEmailRequired) {
    return CheckoutStep.EMAIL_REQUIRED;
  }
  if (dialogData.creditPacks.length > 0) {
    return CheckoutStep.CREDIT_PACKS;
  }
  if (isPaypalAvailable(dialogData)) {
    return CheckoutStep.PAYMENT_OPTIONS;
  }
  return CheckoutStep.STRIPE_EMBEDDED;
};

export const isPaypalAvailable = (dialogData: CheckoutDialogData | null): boolean => {
  return dialogData !== null && dialogData.paypalEnvironment !== '';
};

/**
 * The step the header back arrow returns to, or null when the current step is a point of no return
 * (finalizing, confirmation, error) or the flow's first step.
 */
export const getPreviousStep = (step: CheckoutStep, dialogData: CheckoutDialogData | null, hasAddresses: boolean): CheckoutStep | null => {
  if (dialogData === null) {
    return null;
  }

  switch (step) {
    case CheckoutStep.STRIPE_EMBEDDED:
      if (dialogData.creditPacks.length > 0) {
        return CheckoutStep.CREDIT_PACKS;
      }
      return isPaypalAvailable(dialogData) ? CheckoutStep.PAYMENT_OPTIONS : null;
    case CheckoutStep.BILLING_ADDRESS_LIST:
      return CheckoutStep.PAYMENT_OPTIONS;
    case CheckoutStep.BILLING_ADDRESS_FORM:
      return hasAddresses ? CheckoutStep.BILLING_ADDRESS_LIST : CheckoutStep.PAYMENT_OPTIONS;
    case CheckoutStep.PREFLIGHT:
    case CheckoutStep.EMAIL_REQUIRED:
    case CheckoutStep.CREDIT_PACKS:
    case CheckoutStep.PAYMENT_OPTIONS:
    case CheckoutStep.FINALIZING:
    case CheckoutStep.PREMIUM_CONFIRMATION:
    case CheckoutStep.ERROR:
      return null;
    default:
      return assertNever(step);
  }
};

export const isDownloadPurchase = (mode: CheckoutMode | null): boolean => {
  return mode === CheckoutMode.DOWNLOAD;
};

export const isCreditPackPurchase = (mode: CheckoutMode | null): boolean => {
  return mode === CheckoutMode.CREDIT_PACK;
};

export const isSubscriptionPurchase = (mode: CheckoutMode | null): boolean => {
  return mode === CheckoutMode.SUBSCRIPTION;
};

export const getCheckoutModalTitle = (request: CheckoutRequest | null, step: CheckoutStep): string => {
  if (request === null) {
    return window.i18next.t('pmwjs_checkout');
  }

  if (step === CheckoutStep.PREMIUM_CONFIRMATION) {
    return getConfirmationTitle(request);
  }

  if (request.mode === CheckoutMode.CREDIT_PACK && step === CheckoutStep.CREDIT_PACKS) {
    return request.modalTitle === '' ? window.i18next.t('pmwjs_select_a_credit_pack') : request.modalTitle;
  }

  return window.i18next.t('pmwjs_checkout');
};

export const getConfirmationTitle = (request: CheckoutRequest): string => {
  if (request.mode === CheckoutMode.WELCOME_TO_TEAM) {
    return window.i18next.t('pmwjs_welcome_to_premium');
  }
  if (request.mode === CheckoutMode.SUBSCRIPTION && request.isTrial) {
    return window.i18next.t('pmwjs_your_premium_trial_has_started');
  }
  if (request.mode === CheckoutMode.SUBSCRIPTION && request.isResubscribe) {
    return window.i18next.t('pmwjs_welcome_back_to_premium');
  }
  return window.i18next.t('pmwjs_welcome_to_premium');
};

export const getFinalizingTitle = (mode: CheckoutMode | null): string => {
  if (isSubscriptionPurchase(mode)) {
    return window.i18next.t('pmwjs_finalizing_your_subscription');
  }
  if (isCreditPackPurchase(mode)) {
    return window.i18next.t('pmwjs_finalizing_your_credits');
  }
  return window.i18next.t('pmwjs_finalizing_your_order');
};

export const getFinalizingRedirectUrl = (mode: CheckoutMode | null, pollingIds: CheckoutPollingIds): string => {
  if (isSubscriptionPurchase(mode)) {
    if (pollingIds.invoiceId !== '') {
      return window.PMW.util.site_url(`premium/finalizingorder/s?iid=${pollingIds.invoiceId}`);
    }
    return window.PMW.util.site_url(`premium/finalizingorder/s?pid=${pollingIds.paymentIntentId}`);
  }
  if (isDownloadPurchase(mode)) {
    return window.PMW.util.site_url(`cart/finalizingorder?pid=${pollingIds.paymentIntentId}`);
  }
  return window.PMW.util.site_url(`premium/finalizingorder/c?pid=${pollingIds.paymentIntentId}`);
};

export const getFinalizingText = (mode: CheckoutMode | null, pollingIds: CheckoutPollingIds): string => {
  const langKey = isDownloadPurchase(mode) ? 'pmwjs_transition_to_download_message' : 'pmwjs_redirect_message';
  return window.i18next.t(langKey, {time: CHECKOUT_REDIRECT_LINK_DISABLED_SECONDS, link: getFinalizingRedirectUrl(mode, pollingIds)});
};

export const getPaymentFailedTitle = (request: CheckoutRequest | null): string => {
  if (request !== null && request.mode === CheckoutMode.SUBSCRIPTION && request.isTrial) {
    return window.i18next.t('pmwjs_failed_to_subscribe_to_trial');
  }
  return window.i18next.t('pmwjs_payment_failed');
};

export const getTrialSourceForRequest = (request: SubscriptionCheckoutRequest, defaultSource: string): string => {
  if (!request.isTrial) {
    return '';
  }
  return request.trialSource !== '' ? request.trialSource : defaultSource;
};

/** Credit-pack prices are whole numbers outside the app stores, and the hand-off shows no trailing ".00". */
export const formatCreditPackPrice = (amount: number, currencyCode: string): string => {
  return new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: window.PMW.util.getMaximumFractionDigitsForCurrencyCode(currencyCode),
    minimumFractionDigits: Number.isInteger(amount) ? 0 : undefined,
  }).format(amount);
};

export const getCardPaymentOptionTitle = (dialogData: CheckoutDialogData): string => {
  if (dialogData.isUserInNL) {
    return window.i18next.t('pmwjs_pay_with_card_or_ideal');
  }
  if (dialogData.isUserInIndia) {
    return window.i18next.t('pmwjs_pay_with_card_or_upi');
  }
  return window.i18next.t('pmwjs_pay_with_card');
};
